import { useMemo } from "react";

import { ADMIN_USER_ID } from "@remar/shared/dist/constants";
import { forEach } from "lodash";
import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";

import { getFullState } from "store/features/Auth/auth.slice";

import { DEFAULT_ACCESS, IAccess, PermissionType } from "./permissions";

const usePermissions = (manualPath?: string): IAccess => {
	const { pathname } = useLocation();
	const path = manualPath || pathname;
	const { user, isLoggedIn, userAccess } = useSelector(getFullState);

	const permissions = useMemo<IAccess>(() => {
		if (!user || !userAccess || !isLoggedIn) return DEFAULT_ACCESS;

		const { assignedUserTypes } = user;
		if (!assignedUserTypes || !assignedUserTypes.length) return DEFAULT_ACCESS;

		const assignedUserTypeIds = assignedUserTypes.map(type => type.id);
		const hasSuperAdminAccess = assignedUserTypeIds.includes(ADMIN_USER_ID);

		if (hasSuperAdminAccess) {
			return {
				canEdit: true,
				canView: true,
				accessAreas: [],
				isAccessReady: true
			};
		}

		const accessAreas: PermissionType[] = [];

		forEach(assignedUserTypeIds, typeId => {
			const typeAccess = userAccess[typeId];
			if (!typeAccess) return;

			forEach(typeAccess.accessAreas, access => {
				const index = accessAreas.findIndex(area => area.path === access.path);
				if (index > -1) {
					const existing = accessAreas[index];
					accessAreas[index] = {
						...existing,
						canEdit: existing.canEdit || access.canEdit,
						canView: existing.canView || access.canView
					};
				} else {
					accessAreas.push(access);
				}
			});
		});

		const currentPathAccess = accessAreas.find(area => matchPath(path, { path: area.path, exact: false }));
		if (!currentPathAccess) return DEFAULT_ACCESS;

		const { canEdit, canView } = currentPathAccess;

		return {
			canEdit,
			canView,
			accessAreas,
			isAccessReady: accessAreas.length > 0
		};
	}, [user, userAccess, isLoggedIn, path]);

	return permissions;
};

export default usePermissions;
